<template>
  <!--
    Pop-up do movimento negado (D38/D43): só EXPLICA o bloqueio, não há liberação manual.
    Sem aviso ativo ao gestor na onda 1 (D68): nenhuma cópia diz que o gestor é avisado.
  -->
  <Modal :modal-active="modelValue" has-header-border max-width="max-w-[520px]" @close="close">
    <template #header>
      <div class="flex items-start gap-3 text-left pr-8">
        <div class="w-10 h-10 shrink-0 rounded-full bg-red-50 flex items-center justify-center">
          <svg viewBox="0 0 24 24" class="w-6 h-6 text-negative" aria-hidden="true">
            <path :d="mdiLockAlertOutline" fill="currentColor" />
          </svg>
        </div>
        <div>
          <h2 class="text-lg font-bold text-textPrimary">
            Não dá para mover para “{{ PRE_ALLOCATION_COLUMN_LABEL[targetColumn] }}”
          </h2>
          <p class="text-sm text-[#52526f]">
            {{ professionalName }} ainda tem documento que bloqueia a alocação definitiva.
          </p>
        </div>
      </div>
    </template>

    <div class="flex flex-col gap-4 text-left" data-testid="pre-allocation-blocked-modal">
      <div v-if="items.length" class="flex flex-col gap-2">
        <p class="text-sm font-bold text-textPrimary">O que falta</p>
        <div
          v-for="item in items"
          :key="item.key"
          class="flex items-start justify-between gap-3 rounded-lg border border-[#e8e8ed] px-3 py-2"
        >
          <div>
            <p class="text-sm font-semibold text-textPrimary">{{ item.name }}</p>
            <p v-if="item.detail" class="text-xs text-negative">{{ item.detail }}</p>
          </div>
          <span
            class="shrink-0 text-xs font-semibold"
            :class="isInReview(item) ? 'text-[#9a6b00]' : 'text-negative'"
          >
            {{ isInReview(item) ? 'Em Análise — depende do time' : 'Pendente — depende do médico' }}
          </span>
        </div>
      </div>
      <p v-else class="text-sm text-[#52526f]" data-testid="pre-allocation-blocked-fallback">
        Ainda há documento que bloqueia a alocação definitiva. Atualize a página para ver o que
        falta.
      </p>

      <div class="rounded-lg bg-[#f0f0f4] p-3 text-[13px] text-[#52526f] flex flex-col gap-1.5">
        <p>
          <b class="text-textPrimary">As colunas andam sozinhas.</b> A demanda vai para Resolvido
          quando todos os documentos exigidos estiverem Concluídos — aí a pré-alocação vira
          definitiva e o médico recebe push.
        </p>
        <p>Não existe liberação manual: documento pendente sempre bloqueia o “Resolvido”.</p>
      </div>

      <p class="text-xs text-[#7f7f8e]">
        <template v-if="nearestStation">
          Plantão mais próximo: {{ formatStationStart(nearestStation.start) }} ({{
            nearestStation.scheduleName
          }}).
        </template>
        Sem resolução até 24h antes do plantão, o médico é avisado; no início do plantão o check-in
        fica bloqueado e o gestor decide manter ou remover. Nada é desalocado automaticamente.
      </p>
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row sm:justify-end gap-3">
        <OutlineButton
          v-if="hasInReview"
          text="Revisar documentos"
          width="200px"
          height="40px"
          data-testid="pre-allocation-blocked-review"
          @click="emit('review')"
        />
        <FilledButton
          text="Entendi"
          width="140px"
          height="40px"
          data-testid="pre-allocation-blocked-ok"
          @click="close"
        />
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { mdiLockAlertOutline } from '@mdi/js'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { ProfessionalDocumentStatus } from '@/types/professional-documents.types'
import type { PreAllocationColumn } from '@/types/qualification-eligibility.types'

import { PRE_ALLOCATION_COLUMN_LABEL, formatStationStart } from '../model'
import type { PreAllocationStation } from '../model'

export interface BlockedItem {
  key: string
  name: string
  status: ProfessionalDocumentStatus
  /** Por que está Pendente ("Nunca enviado", "Vencido", "Reprovado"); vazio no Em Análise. */
  detail: string
}

const props = defineProps<{
  modelValue: boolean
  professionalName: string
  targetColumn: PreAllocationColumn
  /** Vazio = o backend não mandou a lista (antigo): mostra o texto de fallback. */
  items: BlockedItem[]
  nearestStation: PreAllocationStation | null
}>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; review: [] }>()

const isInReview = (item: BlockedItem) => item.status === ProfessionalDocumentStatus.IN_REVIEW
const hasInReview = computed(() => props.items.some(isInReview))

function close() {
  emit('update:modelValue', false)
}
</script>
